import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Testimonial } from '@/types/api'
import { Edit, MoreVertical, Plus, Star, Trash2 } from 'lucide-react'
import React from 'react'

interface TestimonialListProps {
  className?: string
  testimonials: Testimonial[]
  isLoading?: boolean
  searchTerm?: string
  currentPage?: number
  itemsPerPage?: number
  totalItems?: number
  totalPages?: number
  onSearchChange?: (_e: React.ChangeEvent<HTMLInputElement>) => void
  onPageChange?: (_page: number) => void
  onAddTestimonial: () => void
  onEditTestimonial: (_testimonial: Testimonial) => void
  onDeleteTestimonial: (_id: string) => void
  onToggleStatus: (_id: string) => void
}

export const TestimonialList: React.FC<TestimonialListProps> = ({
  className,
  testimonials,
  isLoading = false,
  searchTerm = '',
  currentPage = 1,
  itemsPerPage = 10,
  totalItems = 0,
  totalPages = 0,
  onSearchChange,
  onPageChange,
  onAddTestimonial,
  onEditTestimonial,
  onDeleteTestimonial,
  onToggleStatus,
}) => {
  const formatDate = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('id-ID')
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Manual Testimonials</CardTitle>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-sm">
            <Input
              placeholder="Search testimonials..."
              value={searchTerm}
              onChange={onSearchChange}
              className="w-full"
            />
          </div>
          <Button onClick={onAddTestimonial}>
            <Plus className="h-4 w-4 mr-2" />
            Add Testimonial
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="mb-4 text-sm text-muted-foreground">Loading testimonials...</div>
        )}

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Author</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {testimonials.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No testimonials found
                  </TableCell>
                </TableRow>
              ) : (
                testimonials.map((testimonial) => (
                  <TableRow key={testimonial.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {testimonial.avatar_url && (
                          <img
                            src={testimonial.avatar_url}
                            alt={testimonial.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <div className="flex flex-col">
                          <span>{testimonial.name}</span>
                          <span className="text-xs text-muted-foreground">{testimonial.title}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center text-yellow-500">
                        <span className="text-foreground text-sm mr-1 font-semibold">
                          {testimonial.rating}
                        </span>
                        <Star className="w-4 h-4 fill-current" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="line-clamp-2 max-w-xs text-muted-foreground">
                        {testimonial.content}
                      </p>
                    </TableCell>
                    <TableCell>{testimonial.sort_order}</TableCell>
                    <TableCell>
                      <Badge
                        variant={testimonial.is_active ? 'default' : 'secondary'}
                        className="cursor-pointer"
                        onClick={() => onToggleStatus(testimonial.id)}
                      >
                        {testimonial.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(testimonial.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditTestimonial(testimonial)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onDeleteTestimonial(testimonial.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange || (() => {})}
              onItemsPerPageChange={() => {}}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
